import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getToken } from '../api/auth';
import PageContainer from '../components/PageContainer';

interface SettingsData {
  system_prompt_ar: string;
  system_prompt_en: string;
  fallback_message_ar: string;
  fallback_message_en: string;
  error_message_ar: string;
  error_message_en: string;
  widget_title_ar: string;
  widget_title_en: string;
  widget_subtitle_ar: string;
  widget_subtitle_en: string;
  max_conversation_history: number;
  confidence_threshold: number;
  max_tokens_per_response: number;
  temperature: number;
  enable_citations: boolean;
  enable_follow_up_suggestions: boolean;
  enable_intent_classification: boolean;
  enable_safety_filter: boolean;
  rate_limit_enabled: boolean;
  rate_limit_per_minute: number;
  rate_limit_per_hour: number;
  default_contact_name_ar: string;
  default_contact_name_en: string;
  default_contact_phone: string;
  default_contact_email: string;
  default_contact_hours_ar: string;
  default_contact_hours_en: string;
  updated_at: string | null;
  updated_by: string | null;
}

// Mirrors GET /api/admin/rate-limit/status (superuser-only).
interface RateLimitStatus {
  enabled: boolean;
  per_minute: number;
  per_hour: number;
  keyed_by: string;
  redis_available: boolean;
  blocked_this_hour: number;
  blocked_previous_hour: number;
  login_per_minute: number;
  login_per_hour: number;
}

type TabId = 'prompts' | 'widget' | 'behavior' | 'contact' | 'ratelimit';

const TABS: { id: TabId; label: string; icon: JSX.Element }[] = [
  {
    id: 'prompts',
    label: 'System Prompts',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 011.037-.443 48.282 48.282 0 005.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
      </svg>
    ),
  },
  {
    id: 'widget',
    label: 'Widget',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
      </svg>
    ),
  },
  {
    id: 'behavior',
    label: 'Behavior',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
      </svg>
    ),
  },
  {
    id: 'contact',
    label: 'Fallback Contact',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
      </svg>
    ),
  },
  {
    id: 'ratelimit',
    label: 'Rate Limiting',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
  },
];

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<SettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('prompts');
  const [rateStatus, setRateStatus] = useState<RateLimitStatus | null>(null);
  const [rateStatusLoading, setRateStatusLoading] = useState(false);
  const [rateStatusError, setRateStatusError] = useState<string | null>(null);
  const [rateStatusAt, setRateStatusAt] = useState<Date | null>(null);

  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    fetchSettings();
  }, []);

  // The live card reflects what the limiter enforces right now (the saved
  // row + Redis), so it is fetched when the tab opens and after each save.
  useEffect(() => {
    if (activeTab === 'ratelimit') fetchRateLimitStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchRateLimitStatus = async () => {
    try {
      setRateStatusLoading(true);
      setRateStatusError(null);
      const res = await axios.get('/api/admin/rate-limit/status', { headers });
      setRateStatus(res.data);
      setRateStatusAt(new Date());
    } catch (err: any) {
      setRateStatusError(err.response?.data?.detail || 'Failed to load rate limit status');
    } finally {
      setRateStatusLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get('/api/admin/settings', { headers });
      setSettings(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Mirrors the server's 422 guards: a blank prompt would be used verbatim
  // and a cleared number (NaN) would be stored as NULL and revert to defaults.
  const validateSettings = (s: SettingsData): string | null => {
    const required: [keyof SettingsData, string][] = [
      ['system_prompt_ar', 'System prompt (Arabic)'],
      ['system_prompt_en', 'System prompt (English)'],
      ['fallback_message_ar', 'Fallback message (Arabic)'],
      ['fallback_message_en', 'Fallback message (English)'],
      ['error_message_ar', 'Error message (Arabic)'],
      ['error_message_en', 'Error message (English)'],
    ];
    for (const [key, label] of required) {
      if (!String(s[key] ?? '').trim()) return `${label} must not be blank`;
    }
    const ranges: [keyof SettingsData, string, number, number][] = [
      ['temperature', 'Temperature', 0, 2],
      ['max_tokens_per_response', 'Max tokens per response', 100, 2000],
      ['confidence_threshold', 'Confidence threshold', 0, 1],
      ['max_conversation_history', 'Max conversation history', 2, 20],
      ['rate_limit_per_minute', 'Requests per minute', 1, 600],
      ['rate_limit_per_hour', 'Requests per hour', 1, 20000],
    ];
    for (const [key, label, min, max] of ranges) {
      const v = s[key] as number;
      if (!Number.isFinite(v) || v < min || v > max) return `${label} must be a number between ${min} and ${max}`;
    }
    if (s.rate_limit_per_hour < s.rate_limit_per_minute) {
      return 'Requests per hour must be greater than or equal to requests per minute';
    }
    return null;
  };

  const saveSettings = async () => {
    if (!settings) return;
    const problem = validateSettings(settings);
    if (problem) {
      setError(problem);
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      const res = await axios.put('/api/admin/settings', settings, { headers });
      setSettings(res.data);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      if (activeTab === 'ratelimit') fetchRateLimitStatus();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof SettingsData, value: any) => {
    if (!settings) return;
    setSettings({ ...settings, [field]: value });
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-primary-200 rounded w-48" />
          <div className="h-64 bg-primary-200 rounded" />
        </div>
      </PageContainer>
    );
  }

  if (!settings) {
    return (
      <PageContainer>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          {error || 'Settings not found. Please run database migrations.'}
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Chatbot Settings"
      subtitle="Configure system prompts, behavior, and widget appearance"
      actions={
        <>
          {settings.updated_at && (
            <span className="text-xs text-primary-400">
              Last updated: {new Date(settings.updated_at).toLocaleString()}
            </span>
          )}
          <button onClick={saveSettings} disabled={saving} className="btn-primary px-5 py-2.5 text-sm font-medium">
            {saving ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Saving...
              </span>
            ) : (
              'Save Changes'
            )}
          </button>
        </>
      }
    >

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}
      {success && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-emerald-700 text-sm flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
          </svg>
          Settings saved successfully! The chatbot will use the new configuration immediately.
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-primary-100 p-1 rounded-xl">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex-1 justify-center
              ${activeTab === tab.id
                ? 'bg-white text-primary-900 shadow-sm'
                : 'text-primary-500 hover:text-primary-700'
              }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border border-primary-200 shadow-sm">
        {activeTab === 'prompts' && (
          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-primary-800 mb-1">System Prompt (English)</h3>
              <p className="text-xs text-primary-400 mb-2">The core instruction that tells the AI how to behave and what tools it can use.</p>
              <textarea
                value={settings.system_prompt_en}
                onChange={(e) => updateField('system_prompt_en', e.target.value)}
                rows={10}
                className="w-full border border-primary-200 rounded-lg px-4 py-3 text-sm font-mono focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-y"
              />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-primary-800 mb-1">System Prompt (Arabic)</h3>
              <p className="text-xs text-primary-400 mb-2">Arabic system prompt used when the user speaks Arabic.</p>
              <textarea
                value={settings.system_prompt_ar}
                onChange={(e) => updateField('system_prompt_ar', e.target.value)}
                rows={10}
                dir="rtl"
                className="w-full border border-primary-200 rounded-lg px-4 py-3 text-sm font-mono focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-y"
              />
            </div>
            <hr className="border-primary-100" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-primary-800 mb-1">Fallback Message (English)</h3>
                <p className="text-xs text-primary-400 mb-2">Shown when the bot can't find an answer.</p>
                <textarea
                  value={settings.fallback_message_en}
                  onChange={(e) => updateField('fallback_message_en', e.target.value)}
                  rows={4}
                  className="w-full border border-primary-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-y"
                />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-primary-800 mb-1">Fallback Message (Arabic)</h3>
                <p className="text-xs text-primary-400 mb-2">Shown when the bot can't find an answer (Arabic).</p>
                <textarea
                  value={settings.fallback_message_ar}
                  onChange={(e) => updateField('fallback_message_ar', e.target.value)}
                  rows={4}
                  dir="rtl"
                  className="w-full border border-primary-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-y"
                />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-primary-800 mb-1">Error Message (English)</h3>
                <p className="text-xs text-primary-400 mb-2">Shown when a system error occurs.</p>
                <textarea
                  value={settings.error_message_en}
                  onChange={(e) => updateField('error_message_en', e.target.value)}
                  rows={3}
                  className="w-full border border-primary-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-y"
                />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-primary-800 mb-1">Error Message (Arabic)</h3>
                <p className="text-xs text-primary-400 mb-2">Shown when a system error occurs (Arabic).</p>
                <textarea
                  value={settings.error_message_ar}
                  onChange={(e) => updateField('error_message_ar', e.target.value)}
                  rows={3}
                  dir="rtl"
                  className="w-full border border-primary-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-y"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'widget' && (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-primary-800 mb-1">Widget Title (English)</label>
                <input
                  type="text"
                  value={settings.widget_title_en || ''}
                  onChange={(e) => updateField('widget_title_en', e.target.value)}
                  className="w-full border border-primary-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary-800 mb-1">Widget Title (Arabic)</label>
                <input
                  type="text"
                  value={settings.widget_title_ar || ''}
                  onChange={(e) => updateField('widget_title_ar', e.target.value)}
                  dir="rtl"
                  className="w-full border border-primary-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary-800 mb-1">Widget Subtitle (English)</label>
                <input
                  type="text"
                  value={settings.widget_subtitle_en || ''}
                  onChange={(e) => updateField('widget_subtitle_en', e.target.value)}
                  className="w-full border border-primary-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary-800 mb-1">Widget Subtitle (Arabic)</label>
                <input
                  type="text"
                  value={settings.widget_subtitle_ar || ''}
                  onChange={(e) => updateField('widget_subtitle_ar', e.target.value)}
                  dir="rtl"
                  className="w-full border border-primary-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'behavior' && (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-primary-800 mb-1">Temperature</label>
                <p className="text-xs text-primary-400 mb-2">Controls randomness. Lower = more focused, higher = more creative. (0.0 - 2.0)</p>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={settings.temperature}
                    onChange={(e) => updateField('temperature', parseFloat(e.target.value))}
                    className="flex-1 accent-primary"
                  />
                  <span className="text-sm font-mono text-primary-600 w-10 text-center">{settings.temperature}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary-800 mb-1">Max Tokens per Response</label>
                <p className="text-xs text-primary-400 mb-2">Maximum length of the AI response. (100 - 2000)</p>
                <input
                  type="number"
                  min={100}
                  max={2000}
                  step={50}
                  value={settings.max_tokens_per_response}
                  onChange={(e) => updateField('max_tokens_per_response', parseInt(e.target.value))}
                  className="w-full border border-primary-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary-800 mb-1">Confidence Threshold</label>
                <p className="text-xs text-primary-400 mb-2">Minimum confidence before the bot shows a fallback. (0.0 - 1.0)</p>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settings.confidence_threshold}
                    onChange={(e) => updateField('confidence_threshold', parseFloat(e.target.value))}
                    className="flex-1 accent-primary"
                  />
                  <span className="text-sm font-mono text-primary-600 w-10 text-center">{settings.confidence_threshold}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary-800 mb-1">Max Conversation History</label>
                <p className="text-xs text-primary-400 mb-2">Number of previous messages to include as context. (2 - 20)</p>
                <input
                  type="number"
                  min={2}
                  max={20}
                  value={settings.max_conversation_history}
                  onChange={(e) => updateField('max_conversation_history', parseInt(e.target.value))}
                  className="w-full border border-primary-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>
            </div>

            <hr className="border-primary-100" />

            <div>
              <h3 className="text-sm font-semibold text-primary-800 mb-4">Feature Toggles</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {([
                  { key: 'enable_safety_filter' as keyof SettingsData, label: 'Safety Filter', desc: 'Block harmful or inappropriate messages' },
                  { key: 'enable_intent_classification' as keyof SettingsData, label: 'Intent Classification', desc: 'Auto-classify user intent for smarter routing' },
                  { key: 'enable_citations' as keyof SettingsData, label: 'Citations', desc: 'Show source references in responses' },
                  { key: 'enable_follow_up_suggestions' as keyof SettingsData, label: 'Follow-up Suggestions', desc: 'Show suggested follow-up questions' },
                ]).map((toggle) => (
                  <label key={toggle.key} className="flex items-start gap-3 p-3 rounded-lg border border-primary-100 hover:border-primary-200 cursor-pointer transition-colors">
                    <div className="pt-0.5">
                      <input
                        type="checkbox"
                        checked={settings[toggle.key] as boolean}
                        onChange={(e) => updateField(toggle.key, e.target.checked)}
                        className="w-4 h-4 rounded border-primary-300 text-primary-700 focus:ring-primary/20"
                      />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-primary-800">{toggle.label}</div>
                      <div className="text-xs text-primary-400">{toggle.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="p-6 space-y-6">
            <p className="text-sm text-primary-500">Default contact information shown in fallback messages when the bot can't find an answer.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-primary-800 mb-1">Contact Name (English)</label>
                <input
                  type="text"
                  value={settings.default_contact_name_en || ''}
                  onChange={(e) => updateField('default_contact_name_en', e.target.value)}
                  className="w-full border border-primary-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary-800 mb-1">Contact Name (Arabic)</label>
                <input
                  type="text"
                  value={settings.default_contact_name_ar || ''}
                  onChange={(e) => updateField('default_contact_name_ar', e.target.value)}
                  dir="rtl"
                  className="w-full border border-primary-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary-800 mb-1">Phone Number</label>
                <input
                  type="text"
                  value={settings.default_contact_phone || ''}
                  onChange={(e) => updateField('default_contact_phone', e.target.value)}
                  className="w-full border border-primary-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary-800 mb-1">Email Address</label>
                <input
                  type="email"
                  value={settings.default_contact_email || ''}
                  onChange={(e) => updateField('default_contact_email', e.target.value)}
                  className="w-full border border-primary-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary-800 mb-1">Working Hours (English)</label>
                <input
                  type="text"
                  value={settings.default_contact_hours_en || ''}
                  onChange={(e) => updateField('default_contact_hours_en', e.target.value)}
                  className="w-full border border-primary-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary-800 mb-1">Working Hours (Arabic)</label>
                <input
                  type="text"
                  value={settings.default_contact_hours_ar || ''}
                  onChange={(e) => updateField('default_contact_hours_ar', e.target.value)}
                  dir="rtl"
                  className="w-full border border-primary-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'ratelimit' && (
          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-primary-800 mb-4">Public Rate Limiting</h3>
              <label className="flex items-start gap-3 p-3 rounded-lg border border-primary-100 hover:border-primary-200 cursor-pointer transition-colors">
                <div className="pt-0.5">
                  <input
                    type="checkbox"
                    checked={settings.rate_limit_enabled}
                    onChange={(e) => updateField('rate_limit_enabled', e.target.checked)}
                    className="w-4 h-4 rounded border-primary-300 text-primary-700 focus:ring-primary/20"
                  />
                </div>
                <div>
                  <div className="text-sm font-medium text-primary-800">Rate Limiting</div>
                  <div className="text-xs text-primary-400">Cap how many requests each visitor can send to the public chat, documents, feedback and voice endpoints</div>
                </div>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-primary-800 mb-1">Requests per Minute</label>
                <p className="text-xs text-primary-400 mb-2">Per client IP, fixed one-minute window. (1 - 600)</p>
                <input
                  type="number"
                  min={1}
                  max={600}
                  value={settings.rate_limit_per_minute}
                  onChange={(e) => updateField('rate_limit_per_minute', parseInt(e.target.value))}
                  className="w-full border border-primary-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary-800 mb-1">Requests per Hour</label>
                <p className="text-xs text-primary-400 mb-2">Per client IP, must be at least the per-minute value. (1 - 20000)</p>
                <input
                  type="number"
                  min={1}
                  max={20000}
                  value={settings.rate_limit_per_hour}
                  onChange={(e) => updateField('rate_limit_per_hour', parseInt(e.target.value))}
                  className="w-full border border-primary-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>
            </div>

            <div className="rounded-lg bg-primary-50 border border-primary-100 p-4">
              <h4 className="text-sm font-semibold text-primary-800 mb-2">How it works</h4>
              <ul className="list-disc pl-5 space-y-1 text-xs text-primary-600">
                <li>Limits apply per client IP address. A visitor who exceeds either limit gets HTTP 429 (Too Many Requests) until the window resets.</li>
                <li>Many students behind one campus NAT share a single IP, so they share one bucket. Keep the per-minute value generous so a busy lab is not blocked as one visitor.</li>
                <li>Turning rate limiting off removes abuse protection for the public chat. The admin login keeps its own fixed limit{rateStatus ? ` (${rateStatus.login_per_minute} attempts per minute, ${rateStatus.login_per_hour} per hour)` : ''} regardless of this switch.</li>
                <li>Counters live in Redis. If Redis is unreachable the limiter lets requests through rather than taking the chatbot down, so watch the Redis status below.</li>
              </ul>
            </div>

            <div className="rounded-lg border border-primary-100 p-4">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h4 className="text-sm font-semibold text-primary-800">Live Status</h4>
                  <p className="text-xs text-primary-400">
                    The saved settings and the Redis counters (unsaved edits above are not included). Every server worker applies a saved change within a minute.
                    {rateStatusAt && ` Checked at ${rateStatusAt.toLocaleTimeString()}.`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={fetchRateLimitStatus}
                  disabled={rateStatusLoading}
                  className="btn-secondary px-3 py-1.5 text-xs font-medium flex-shrink-0"
                >
                  {rateStatusLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
              {rateStatusError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-xs mb-3">{rateStatusError}</div>
              )}
              {rateStatus && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {([
                    {
                      label: 'Limiter',
                      value: rateStatus.enabled ? 'On' : 'Off',
                      desc: rateStatus.enabled
                        ? `${rateStatus.per_minute}/min, ${rateStatus.per_hour}/hour per IP`
                        : 'Public endpoints are not rate limited',
                      warn: !rateStatus.enabled,
                    },
                    {
                      label: 'Redis',
                      value: rateStatus.redis_available ? 'Connected' : 'Not connected',
                      desc: !rateStatus.redis_available
                        ? 'Limits cannot be enforced (fail open)'
                        : rateStatus.enabled ? 'Limits are being enforced' : 'Ready to enforce once switched on',
                      warn: !rateStatus.redis_available,
                    },
                    {
                      label: 'Blocked this hour',
                      value: String(rateStatus.blocked_this_hour),
                      desc: 'Public requests answered with 429',
                      warn: false,
                    },
                    {
                      label: 'Blocked previous hour',
                      value: String(rateStatus.blocked_previous_hour),
                      desc: 'Hourly buckets, UTC',
                      warn: false,
                    },
                  ]).map((tile) => (
                    <div key={tile.label} className={`rounded-lg border p-3 ${tile.warn ? 'border-amber-200 bg-amber-50' : 'border-primary-100 bg-white'}`}>
                      <div className="text-xs text-primary-400">{tile.label}</div>
                      <div className={`text-lg font-semibold ${tile.warn ? 'text-amber-700' : 'text-primary-900'}`}>{tile.value}</div>
                      <div className="text-xs text-primary-500">{tile.desc}</div>
                    </div>
                  ))}
                </div>
              )}
              {!rateStatus && !rateStatusError && rateStatusLoading && (
                <div className="animate-pulse h-16 bg-primary-100 rounded-lg" />
              )}
            </div>
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default Settings;
